import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import Pagination from '../components/Pagination';
import ProgressChart from '../components/ProgressChart';
import SnapshotsTable from '../components/SnapshotsTable';
import TagAutocomplete from '../components/TagAutocomplete';
import TransferOwnerDialog from '../components/TransferOwnerDialog';
import TreeView from '../components/TreeView';
import VoteControl from '../components/VoteControl';
import { fmtElapsed, indexerState } from '../lib/indexerState';
import { recrawlState } from '../lib/recrawlState';
import type { Archive, ArchiveMetricsResponse, ArchiveSiteType, ArchiveTag, CrawlErrorsResponse } from '../types';

type TabKey = 'snapshots' | 'errors' | 'tree';

function fmtSec(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function ArchiveView() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [archive, setArchive] = useState<Archive | null>(null);
  const [tab, setTab] = useState<TabKey>('snapshots');

  useEffect(() => {
    if (!id) return;
    api<{ data: Archive }>(`/archives/${id}`).then(({ data }) => setArchive(data));
  }, [id]);

  // Reflect the archive root in the browser tab title. Restore to
  // the static default when the page unmounts so back-navigation
  // doesn't leave a stale archive name in the tab.
  useEffect(() => {
    if (!archive) return;
    const prev = document.title;
    document.title = `Archive - ${archive.domain}${archive.path_prefix ?? ''}`;
    return () => { document.title = prev; };
  }, [archive?.domain, archive?.path_prefix]);

  // While the search indexer is running, re-poll the archive every 10s so
  // the "X of Y URLs" line moves on its own. The poll auto-stops when
  // text_indexing_started_at clears (the next refresh sees null and the
  // dependency change shuts the interval down).
  useEffect(() => {
    if (!id || !archive?.text_indexing_started_at) return;
    const tick = window.setInterval(() => {
      api<{ data: Archive }>(`/archives/${id}`).then(({ data }) => setArchive(data));
    }, 10_000);
    return () => window.clearInterval(tick);
  }, [id, archive?.text_indexing_started_at]);

  // Same pattern for recrawl: poll while a run is in flight so the
  // scanned-count line moves. recrawl_finished_at landing flips the
  // dependency and shuts the interval down.
  const recrawlInFlight = archive?.recrawl_started_at && !archive?.recrawl_finished_at;
  useEffect(() => {
    if (!id || !recrawlInFlight) return;
    const tick = window.setInterval(() => {
      api<{ data: Archive }>(`/archives/${id}`).then(({ data }) => setArchive(data));
    }, 10_000);
    return () => window.clearInterval(tick);
  }, [id, recrawlInFlight]);

  if (!archive) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="card">
        <h2>
          {archive.domain}{archive.path_prefix}
          {archive.needs_local_worker && (
            <span title="Cloudflare or similar challenge — needs the local-worker lane"
              style={{ marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 999, background: '#ffe', border: '1px solid #cc8', color: '#770', verticalAlign: 'middle' }}>
              needs local worker
            </span>
          )}
        </h2>
        <p className="muted">
          Seed: <a href={archive.seed_url} target="_blank" rel="noreferrer">{archive.seed_url}</a>
          {archive.seed_replay_url && (
            <> {' · '}
              <a href={archive.seed_replay_url} target="_blank" rel="noopener">
                View archived
              </a>
            </>
          )}
        </p>
        <ArchiveSearchBox archive={archive} />
        <ArchiveTags archiveId={archive.id} canManage={isAdmin} />
        {archive.text_indexed_at !== undefined && (() => {
          const idx = indexerState(archive);
          if (idx.state === 'running') {
            return (
              <p style={{ fontSize: 12, marginTop: -4 }}>
                <span style={{ color: '#057' }}>
                  Indexing for search now
                  {archive.text_index_progress
                    ? <> ({archive.text_index_progress.indexed.toLocaleString()} of {archive.text_index_progress.total.toLocaleString()} URLs, started {new Date(idx.startedAt).toLocaleTimeString()})…</>
                    : <> (started {new Date(idx.startedAt).toLocaleTimeString()})…</>}
                </span>
              </p>
            );
          }
          if (idx.state === 'stalled') {
            return (
              <p style={{ fontSize: 12, marginTop: -4 }}>
                <span style={{ color: '#c33' }}>
                  Indexer appears stalled — no activity for {fmtElapsed(idx.staleForMs)}.
                  {archive.text_index_progress
                    ? <> Stopped at {archive.text_index_progress.indexed.toLocaleString()} of {archive.text_index_progress.total.toLocaleString()} URLs.</>
                    : null}
                  {' '}Use the Restart button to re-spawn the subprocess.
                </span>
              </p>
            );
          }
          return (
            <p style={{ fontSize: 12, marginTop: -4 }} className="muted">
              {archive.text_indexed_at
                ? <>Search index built {new Date(archive.text_indexed_at).toLocaleString()}.</>
                : <>Not indexed for search yet — the search box above won't return results until an admin runs an index pass.</>}
            </p>
          );
        })()}
        {(() => {
          const rc = recrawlState(archive);
          if (rc.state === 'idle') return null;
          if (rc.state === 'running') {
            return (
              <p style={{ fontSize: 12, marginTop: -4 }}>
                <span style={{ color: '#057' }}>
                  Recrawling now — {rc.scanned.toLocaleString()} snapshots scanned,
                  {' '}{rc.followups.toLocaleString()} new URLs queued (started{' '}
                  {new Date(rc.startedAt).toLocaleTimeString()})…
                </span>
              </p>
            );
          }
          if (rc.state === 'stalled') {
            return (
              <p style={{ fontSize: 12, marginTop: -4 }}>
                <span style={{ color: '#c33' }}>
                  Recrawl appears stalled — no progress for {fmtElapsed(rc.staleForMs)}.
                  {' '}Stopped at {rc.scanned.toLocaleString()} snapshots scanned,
                  {' '}{rc.followups.toLocaleString()} new URLs queued.
                  {' '}Click Recrawl to spawn a fresh subprocess.
                </span>
              </p>
            );
          }
          return (
            <p style={{ fontSize: 12, marginTop: -4 }} className="muted">
              Last recrawl finished {new Date(rc.finishedAt).toLocaleString()}
              {' '}— {rc.scanned.toLocaleString()} snapshots scanned,
              {' '}{rc.followups.toLocaleString()} new URLs queued.
            </p>
          );
        })()}
        <p style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className={`status-pill ${archive.status}`}>{archive.status}</span>
          <span className="muted">
            {archive.pages_fetched.toLocaleString()} pages,
            {archive.queued_count != null && (
              <> {archive.queued_count.toLocaleString()} queued,</>
            )}
            {' '}{archive.errors_count.toLocaleString()} errors,
          </span>
          {isAdmin && archive.status === 'deleting' && (
            <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="spinner" role="status" aria-label="Deleting" />
              Deletion in progress — purging storage in the background.
            </span>
          )}
          {isAdmin && archive.status !== 'deleting' && (
            <>
              <RateEditor
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              <MaxParallelEditor
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              <VerifySslToggle
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              <StripReplayScriptsToggle
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              <UseBrowserUaToggle
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              <SkipBinariesToggle
                archive={archive}
                onUpdated={(a) => setArchive(a)}
              />
              {(archive.status === 'pending' || archive.status === 'crawling' || archive.status === 'degraded' || archive.status === 'retry_run') && (
                <button
                  className="secondary"
                  title="Stop fetching this archive's queue. Queue rows are kept; Resume picks up where it left off."
                  onClick={async () => {
                  const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/pause`, { method: 'POST' });
                  setArchive(data);
                }}>Pause</button>
              )}
              {(archive.status === 'paused' || archive.status === 'degraded') && (
                <button
                  title={archive.status === 'degraded'
                    ? 'Reset the degraded backoff and try the next fetch immediately.'
                    : 'Resume fetching the queue. Picks up from where Pause left off.'}
                  onClick={async () => {
                  const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/resume`, { method: 'POST' });
                  setArchive(data);
                }}>{archive.status === 'degraded' ? 'Force probe' : 'Resume'}</button>
              )}
              {(archive.status === 'done' || archive.status === 'error') && (
                <button
                  title="Re-queue transient failures only — timeouts, server 5xx, persistent 429/503, network errors. Permanent failures (404, 403, anti-bot challenges, etc.) stay failed so we don't hammer the site with hopeless retries."
                  onClick={async () => {
                  const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/retry`, { method: 'POST' });
                  setArchive(data);
                }}>Retry transient failures</button>
              )}
              {(archive.status === 'pending' || archive.status === 'crawling' || archive.status === 'degraded' || archive.status === 'retry_run' || archive.status === 'paused') && (
                <button
                  className="secondary"
                  title="Drain pending queue items and stop crawling. Reversible by adding URLs back to the queue."
                  onClick={async () => {
                  if (!window.confirm('Terminate this archive? Pending queue items will be deleted. The archive can be revived by adding URLs to it.')) return;
                  const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/terminate`, { method: 'POST' });
                  setArchive(data);
                }}>Terminate</button>
              )}
              {archive.status === 'terminated' && (
                <button
                  title="Re-seed the seed URL into the queue, flip status to crawling, and kick off a background recrawl that re-populates the queue from existing snapshots. Snapshots dedup by content, so re-fetched bodies that haven't changed cost only bandwidth."
                  onClick={async () => {
                    if (!window.confirm('Resume this terminated archive? The seed URL goes back in the queue, status flips to crawling, and a background recrawl repopulates the queue from existing snapshots. Re-fetched URLs that haven\'t changed don\'t take extra storage.')) return;
                    const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/resume-terminated`, { method: 'POST' });
                    setArchive(data);
                  }}
                >Resume crawl</button>
              )}
              <BackfillImagesButton archiveId={archive.id} />
              <BackfillImagesButton archiveId={archive.id} force />
              <RecrawlButton archive={archive} onUpdated={setArchive} />
              <IndexForSearchButton archive={archive} />
              <TransferOwnerButton archive={archive} onUpdated={setArchive} />
              <DeleteArchiveButton archive={archive} />
            </>
          )}
        </p>
        {isAdmin && archive.owner && (
          <p style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
            <span className="muted">Owner: </span>
            <Link to={`/admin/users/${archive.owner.id}`}>{archive.owner.name}</Link>
            <span className="muted"> &lt;{archive.owner.email}&gt;</span>
          </p>
        )}
        {(isAdmin || user?.id === archive.created_by) && (
          <VisibilityControl archive={archive} onUpdated={setArchive} />
        )}
        {archive.status === 'degraded' && (
          <p style={{ color: '#850', fontSize: 13, marginTop: 8 }}>
            Backing off {fmtSec(archive.degraded_backoff_sec)} between requests after
            {' '}{archive.consecutive_errors} consecutive transient failures.
            Will recover after 3 successes; "Force probe" resets immediately.
          </p>
        )}
        {archive.needs_local_worker && (
          <p style={{ color: '#770', background: '#ffe', border: '1px solid #cc8', padding: '8px 12px', borderRadius: 4, fontSize: 13, marginTop: 8 }}>
            {archive.pages_fetched > 0 ? (
              <>
                <strong>Some URLs are behind an anti-bot challenge</strong>
                {' '}(Cloudflare, CrowdSec, or Anubis proof-of-work). Those URLs
                will stay unfetched until a residential-IP / Playwright-based
                worker lane lands — the rest of the crawl proceeds normally.
                Check the Errors tab for which URLs are affected.
              </>
            ) : (
              <>
                <strong>This site is fully behind an anti-bot challenge</strong>
                {' '}(Cloudflare, CrowdSec, or Anubis proof-of-work). The crawler
                can't solve these without a real browser. A residential-IP /
                Playwright-based worker lane is planned but not yet built;
                until then, this archive can't make progress. Terminate to
                remove it from active crawl rotation.
              </>
            )}
            {isAdmin && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Dismiss the anti-bot-challenge flag? It\'ll come back automatically if a future fetch hits another challenge — useful when the originally-blocked URL has since been ignore-listed (e.g. a wp-login URL covered by the WordPress site_type) and the warning is misleading.')) return;
                    const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/clear-local-worker-flag`, { method: 'POST' });
                    setArchive(data);
                  }}
                  className="link-button"
                  style={{ color: '#660', textDecoration: 'underline', background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' }}
                  title="Clears archives.needs_local_worker. The flag is sticky on the worker side and only resets when manually dismissed; if a later fetch hits another challenge it'll set again."
                >
                  Dismiss this warning
                </button>
              </>
            )}
          </p>
        )}
        {archive.retry_runs_count > 0 && (
          <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
            Retry runs: {archive.retry_runs_count}
            {archive.last_retry_run_at && (
              <> (last: {new Date(archive.last_retry_run_at).toLocaleString()})</>
            )}
          </p>
        )}
        {archive.last_error && (
          <p style={{ color: '#900', fontSize: 13, marginTop: 8 }}>
            Last error: {archive.last_error}
          </p>
        )}
      </div>

      {isAdmin && (
        <AddUrlCard
          archiveId={archive.id}
          onArchiveStatusChange={(status) => setArchive((a) => a ? { ...a, status: status as Archive['status'] } : a)}
        />
      )}

      {isAdmin && (
        <UrlExcludeRegexCard
          archive={archive}
          onUpdated={(a) => setArchive(a)}
        />
      )}

      {isAdmin && (
        <AuthControls
          archive={archive}
          onUpdated={(a) => setArchive(a)}
        />
      )}

      <SiteTypesSummary archiveId={archive.id} />

      <TrendCard archiveId={archive.id} archiveStatus={archive.status} />

      <div className="card">
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
          <TabButton active={tab === 'snapshots'} onClick={() => setTab('snapshots')}>
            Snapshots ({archive.pages_fetched.toLocaleString()})
          </TabButton>
          <TabButton active={tab === 'tree'} onClick={() => setTab('tree')}>
            Tree
          </TabButton>
          {archive.errors_count > 0 && (
            <TabButton active={tab === 'errors'} onClick={() => setTab('errors')}>
              Errors ({archive.errors_count.toLocaleString()})
            </TabButton>
          )}
        </div>

        {tab === 'tree' ? (
          <TreeView archiveId={archive.id} />
        ) : tab === 'snapshots' ? (
          <SnapshotsTable archiveId={archive.id} />
        ) : (
          <ErrorsTab archiveId={archive.id} errorsCount={archive.errors_count} />
        )}
      </div>
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={active ? '' : 'secondary'}
      style={{ fontSize: 14, padding: '6px 14px' }}
    >
      {children}
    </button>
  );
}

const LICENSE_TEXT =
  'By making this archive public, you confirm that you own this website or ' +
  'otherwise have the right to archive it and to make the archived copy ' +
  'available to other users of Social Archive. You accept responsibility for ' +
  'the legality of capturing and sharing this content.';

// Owner/admin control for archive visibility. Archives are private by default
// (owner + admins only); going public requires accepting the license, so the
// "Make public" button reveals the agreement first when it hasn't been
// accepted yet (the API also enforces this and returns license_required).
function VisibilityControl({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);
  const [showLicense, setShowLicense] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function errMsg(e: unknown): string {
    if (e instanceof ApiError) {
      return (e.payload as { message?: string } | null)?.message ?? `Failed (${e.status}).`;
    }
    return 'Failed.';
  }

  async function setPublic(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api<{ data: Archive }>(`/my/archives/${archive.id}/public`, {
        method: 'POST',
        body: { public: next },
      });
      onUpdated(data);
      setShowLicense(false);
      setAgreed(false);
    } catch (e) {
      // 422 license_required → reveal the agreement instead of erroring.
      if (e instanceof ApiError && (e.payload as { license_required?: boolean } | null)?.license_required) {
        setShowLicense(true);
      } else {
        setError(errMsg(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function agreeAndPublish() {
    setBusy(true);
    setError(null);
    try {
      await api(`/my/archives/${archive.id}/accept-license`, { method: 'POST' });
      const { data } = await api<{ data: Archive }>(`/my/archives/${archive.id}/public`, {
        method: 'POST',
        body: { public: true },
      });
      onUpdated(data);
      setShowLicense(false);
      setAgreed(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ margin: '4px 0 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 13 }}>Visibility:</span>
        <span className="status-pill"
          style={archive.public ? { background: '#dcfce7', color: '#166534' } : { background: '#eee', color: '#666' }}>
          {archive.public ? 'Public' : 'Private'}
        </span>
        {archive.domain_verified_at && (
          <span className="status-pill" style={{ background: '#dbeafe', color: '#1e40af' }}
            title="Domain ownership verified via DNS TXT record">
            ✓ owner-verified
          </span>
        )}
        {archive.public ? (
          <button className="secondary" disabled={busy} onClick={() => setPublic(false)}>
            {busy ? '…' : 'Make private'}
          </button>
        ) : !showLicense ? (
          <button className="secondary" disabled={busy}
            onClick={() => (archive.license_accepted_at ? setPublic(true) : setShowLicense(true))}>
            {busy ? '…' : 'Make public'}
          </button>
        ) : null}
      </div>

      {showLicense && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, maxWidth: 560, fontSize: 13 }}>
          <p style={{ marginTop: 0 }}>{LICENSE_TEXT}</p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            I own or have the right to archive this site and share it publicly.
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={!agreed || busy} onClick={agreeAndPublish}>
              {busy ? 'Publishing…' : 'Agree & make public'}
            </button>
            <button className="secondary" disabled={busy}
              onClick={() => { setShowLicense(false); setAgreed(false); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <span className="error">{error}</span>}
    </div>
  );
}

// Mirrors SnapshotsTable's search: explicit (button / Enter), Contains/Prefix
// toggle, spinner. Self-contained so it can page + search the /errors endpoint
// without the parent re-fetching. `errorsCount` is a refetch trigger — a retry
// that produces new errors bumps it, surfacing them without a manual reload.
function ErrorsTab({ archiveId, errorsCount }: { archiveId: number; errorsCount: number }) {
  const [data, setData] = useState<CrawlErrorsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [modeInput, setModeInput] = useState<'substring' | 'prefix'>('prefix');
  const [mode, setMode] = useState<'substring' | 'prefix'>('prefix');
  const [loading, setLoading] = useState(false);

  function submitSearch() {
    setQ(qInput.trim());
    setMode(modeInput);
    setPage(1);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<CrawlErrorsResponse>(`/archives/${archiveId}/errors`, {
      query: { page, per_page: 50, q: q || undefined, mode: q ? mode : undefined },
    })
      .then((res) => { if (!cancelled) setData(res); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [archiveId, page, q, mode, errorsCount]);

  if (!data) return <p className="muted">Loading…</p>;

  return (
    <>
      {data.breakdown.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {data.breakdown.map((b) => (
            <span key={`${b.kind}-${b.status_code ?? 'na'}`}
              style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: '#fdd', color: '#900', border: '1px solid #f99' }}>
              {b.kind}{b.status_code ? ` ${b.status_code}` : ''}: {Number(b.count).toLocaleString()}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          type="search"
          placeholder={modeInput === 'prefix' ? 'URL or /path prefix…' : 'Filter URL…'}
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
          style={{ width: 240 }}
        />
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          {(['substring', 'prefix'] as const).map((m) => (
            <label key={m}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: 0, cursor: 'pointer' }}>
              <input
                type="radio"
                name={`errSearchMode-${archiveId}`}
                checked={modeInput === m}
                onChange={() => setModeInput(m)}
              />
              {m === 'substring' ? 'Contains' : 'Prefix'}
            </label>
          ))}
        </span>
        <button onClick={submitSearch} disabled={loading}>Search</button>
        {loading && <span className="spinner" role="status" aria-label="Searching" />}
      </div>

      {data.recent.data.length === 0
        ? <p className="muted">{q ? `No errors match "${q}".` : 'No recorded errors.'}</p>
        : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Kind</th>
                  <th>URL</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.data.map((e) => (
                  <tr key={e.id}>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{new Date(e.occurred_at).toLocaleString()}</td>
                    <td>{e.kind}{e.status_code ? ` ${e.status_code}` : ''}</td>
                    <td style={{ wordBreak: 'break-all' }}>{e.url}</td>
                    <td style={{ wordBreak: 'break-word' }}>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              page={data.recent.current_page}
              lastPage={data.recent.last_page}
              total={data.recent.total}
              perPage={data.recent.per_page}
              onChange={setPage}
            />
          </>
        )}
    </>
  );
}

type QueueResponse =
  | { status: 'queued'; archive_status: string }
  | { status: 'already_archived'; snapshot_count: number; last_captured_at: string }
  | { status: 'already_pending'; queue_status: string };

function AddUrlCard({ archiveId, onArchiveStatusChange }: {
  archiveId: number;
  onArchiveStatusChange: (status: string) => void;
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<QueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(force: boolean) {
    if (!url.trim()) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const resp = await api<QueueResponse>(`/archives/${archiveId}/queue`, {
        method: 'POST',
        body: { url: url.trim(), force },
      });
      setResult(resp);
      if (resp.status === 'queued') onArchiveStatusChange(resp.archive_status);
    } catch (e) {
      const apiErr = e as { status?: number; payload?: { message?: string } };
      setError(apiErr.payload?.message ?? (e instanceof Error ? e.message : 'Failed'));
    } finally {
      setBusy(false);
    }
  }

  function reset() { setResult(null); setError(null); }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 14 }}>Add URL to queue</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        For pages the crawler can't reach via links (dropdown navigation, form-submit pages, isolated subtrees).
        Adding to a done archive reopens it.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="url"
          value={url}
          onChange={(e) => { setUrl(e.target.value); reset(); }}
          placeholder="https://…"
          style={{ flex: 1, minWidth: 280 }}
          disabled={busy}
        />
        <button onClick={() => submit(false)} disabled={busy || !url.trim()}>
          {busy ? 'Working…' : 'Add'}
        </button>
      </div>

      {error && <p style={{ color: '#900', fontSize: 13, marginTop: 8 }}>{error}</p>}

      {result?.status === 'queued' && (
        <p style={{ color: '#070', fontSize: 13, marginTop: 8 }}>
          Queued. Archive is now <strong>{result.archive_status}</strong>.
        </p>
      )}

      {result?.status === 'already_archived' && (
        <div style={{ marginTop: 8, padding: 8, background: '#ffe', border: '1px solid #cc8', borderRadius: 4, fontSize: 13 }}>
          Already archived: <strong>{result.snapshot_count}</strong> capture{result.snapshot_count === 1 ? '' : 's'}
          {result.last_captured_at && <>, last on {new Date(result.last_captured_at).toLocaleString()}</>}.
          <button
            className="secondary"
            onClick={() => submit(true)}
            disabled={busy}
            style={{ marginLeft: 12 }}
          >
            Add anyway
          </button>
        </div>
      )}

      {result?.status === 'already_pending' && (
        <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          Already in the queue ({result.queue_status}). The worker will fetch it.
        </p>
      )}
    </div>
  );
}

function BackfillImagesButton({ archiveId, force = false }: { archiveId: number; force?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const label = force ? 'Re-scan images & frames' : 'Backfill images & frames';
  const tooltip = force
    ? 'Force a re-scan of every stored HTML snapshot, even ones we already processed. Use after policy changes (e.g. cross-origin images enabled, frame extraction added).'
    : 'Walk stored HTML snapshots that haven\'t been image-scanned yet; extract <img>/<iframe>/<frame> URLs and enqueue them.';
  const confirmMsg = force
    ? 'Force re-scan ALL HTML snapshots for image/frame URLs? Use after a scope or policy change. Already-archived URLs won\'t be re-fetched (queue dedup), but the scan itself revisits every page.'
    : 'Backfill image/frame URLs from stored HTML pages? Scans only snapshots that haven\'t been processed yet. Runs in the background.';

  async function run() {
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api<{ status: string }>(`/archives/${archiveId}/backfill-images`, {
        method: 'POST',
        body: { force },
      });
      setStarted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      className="secondary"
      onClick={run}
      disabled={busy || started}
      title={tooltip}
    >
      {started ? 'Started' : busy ? 'Starting…' : label}
    </button>
  );
}

function VerifySslToggle({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);

  async function toggle(checked: boolean) {
    if (!checked && !window.confirm(
      'Disable TLS certificate verification for this archive? '
      + 'Use only when the target site has a broken cert (CN mismatch, expired, self-signed) '
      + 'that you trust anyway. Crawls become vulnerable to MITM redirects on this archive.'
    )) return;
    setBusy(true);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/verify-ssl`, {
        method: 'POST',
        body: { verify_ssl: checked },
      });
      onUpdated(data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 0 }}
      title="When off, the crawler skips TLS certificate verification for this archive. Use only for trusted sites with broken certs."
    >
      <input
        type="checkbox"
        checked={archive.verify_ssl}
        onChange={(e) => toggle(e.target.checked)}
        disabled={busy}
      />
      <span className="muted">verify TLS</span>
    </label>
  );
}

/** Per-archive opt-in: drop every <script> tag from replayed HTML.
 *  Use for sites whose JS destroys the rendered DOM in our replay
 *  context (paywall checks, host assertions, bot beacons). */
function StripReplayScriptsToggle({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);

  async function toggle(checked: boolean) {
    if (checked && !window.confirm(
      'Strip every <script> from this archive\'s replayed HTML? '
      + 'Use for sites whose JS wipes the rendered page in our replay context '
      + '(paywall checks, host assertions, bot beacons). '
      + 'Loses interactive features (lightboxes, infinite scroll); static page content stays intact.'
    )) return;
    setBusy(true);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/strip-replay-scripts`, {
        method: 'POST',
        body: { strip_replay_scripts: checked },
      });
      onUpdated(data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 0 }}
      title="When on, ReplayController drops every <script> from this archive's HTML on replay. Use for sites whose JS destroys the page (paywall / host-assertion / bot-beacon)."
    >
      <input
        type="checkbox"
        checked={archive.strip_replay_scripts}
        onChange={(e) => toggle(e.target.checked)}
        disabled={busy}
      />
      <span className="muted">strip JS on replay</span>
    </label>
  );
}

/** Per-archive opt-in: present a Chrome-on-macOS UA instead of the
 *  SocialArchiveBot string. For sites where being identified as a
 *  bot would draw attention even at polite request rates — typically
 *  authenticated archives where the existing session + bot UA is the
 *  worst-of-both signal. */
function UseBrowserUaToggle({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);

  async function toggle(checked: boolean) {
    setBusy(true);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/use-browser-ua`, {
        method: 'POST',
        body: { use_browser_ua: checked },
      });
      onUpdated(data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 0 }}
      title="When on, the crawler presents a Chrome browser UA instead of the SocialArchiveBot string. Useful for authenticated archives where a bot UA would draw admin attention even at polite rates."
    >
      <input
        type="checkbox"
        checked={archive.use_browser_ua}
        onChange={(e) => toggle(e.target.checked)}
        disabled={busy}
      />
      <span className="muted">browser UA</span>
    </label>
  );
}

function SkipBinariesToggle({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);

  async function toggle(checked: boolean) {
    setBusy(true);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/skip-binaries`, {
        method: 'POST',
        body: { skip_binaries: checked },
      });
      onUpdated(data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 0 }}
      title="When on, the crawler skips images and bulk binaries (zip/pdf/audio/video/etc.) — it won't queue newly-found ones and won't fetch already-queued ones. Stylesheets, scripts, fonts, and inline SVG are still captured. Takes effect immediately, even mid-crawl."
    >
      <input
        type="checkbox"
        checked={archive.skip_binaries}
        onChange={(e) => toggle(e.target.checked)}
        disabled={busy}
      />
      <span className="muted">skip binaries</span>
    </label>
  );
}

function RateEditor({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [value, setValue] = useState(String(archive.requests_per_min));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resync when the archive prop changes (e.g. after Pause/Resume actions
  // that swap the archive object). Without this, the input would stay on
  // the user's last edit even if the underlying RPM came back different.
  useEffect(() => { setValue(String(archive.requests_per_min)); }, [archive.requests_per_min]);

  async function commit() {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n < 1 || n > 600) {
      setError('1–600');
      setValue(String(archive.requests_per_min));
      return;
    }
    if (n === archive.requests_per_min) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/rate`, {
        method: 'POST',
        body: { requests_per_min: n },
      });
      onUpdated(data);
    } catch {
      setError('Failed');
      setValue(String(archive.requests_per_min));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
      <input
        type="number"
        min={1}
        max={600}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        disabled={busy}
        style={{ width: 60, padding: '2px 6px', fontSize: 13 }}
        title="Requests per minute (1–600). Saved on blur."
      />
      <span className="muted">/min</span>
      {busy && <span className="muted" style={{ fontSize: 11 }}>saving…</span>}
      {error && <span style={{ color: '#900', fontSize: 11 }}>{error}</span>}
    </span>
  );
}

function MaxParallelEditor({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [value, setValue] = useState(String(archive.max_parallel));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setValue(String(archive.max_parallel)); }, [archive.max_parallel]);

  async function commit() {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n < 1 || n > 32) {
      setError('1–32');
      setValue(String(archive.max_parallel));
      return;
    }
    if (n === archive.max_parallel) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/max-parallel`, {
        method: 'POST',
        body: { max_parallel: n },
      });
      onUpdated(data);
    } catch {
      setError('Failed');
      setValue(String(archive.max_parallel));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
      <span className="muted">×</span>
      <input
        type="number"
        min={1}
        max={32}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        disabled={busy}
        style={{ width: 50, padding: '2px 6px', fontSize: 13 }}
        title="Max parallel fetches against this archive (1–32). Defaults to 1; bump only if the per-minute budget is high enough that one sequential worker can't saturate it. The per-origin token bucket still caps total RPM."
      />
      <span className="muted">parallel</span>
      {busy && <span className="muted" style={{ fontSize: 11 }}>saving…</span>}
      {error && <span style={{ color: '#900', fontSize: 11 }}>{error}</span>}
    </span>
  );
}

function TrendCard({ archiveId, archiveStatus }: { archiveId: number; archiveStatus: Archive['status'] }) {
  const [metrics, setMetrics] = useState<ArchiveMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<ArchiveMetricsResponse>(`/archives/${archiveId}/metrics`)
      .then((d) => { if (!cancelled) setMetrics(d); })
      .catch(() => { if (!cancelled) setMetrics(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [archiveId]);

  // Hide entirely when there are zero samples — keeps the page clean for
  // archives where the scheduler hasn't fired yet (e.g. just-created).
  if (!loading && (metrics === null || metrics.samples.length === 0)) {
    return null;
  }

  // Sampling skips paused/terminated archives, so any visible chart is
  // necessarily stale and the projection will say "pages aren't increasing"
  // for unhelpful reasons. Surface the actual reason explicitly.
  const isFrozen = archiveStatus === 'paused' || archiveStatus === 'terminated';

  return (
    <div className="card">
      <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 14 }}>Trend</h3>
      {isFrozen && (
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Sampling is paused while this archive is <strong>{archiveStatus}</strong>.
          {archiveStatus === 'paused' && ' Resume the archive to continue collecting datapoints.'}
          {' '}The chart below shows history up to the last sample.
        </p>
      )}
      {loading || !metrics
        ? <p className="muted" style={{ fontSize: 13 }}>Loading…</p>
        : <ProgressChart data={metrics} />
      }
    </div>
  );
}

function SiteTypesSummary({ archiveId }: { archiveId: number }) {
  const [rows, setRows] = useState<ArchiveSiteType[] | null>(null);

  useEffect(() => {
    api<{ data: ArchiveSiteType[] }>(`/archives/${archiveId}/site-types`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [archiveId]);

  if (rows === null || rows.length === 0) return null;

  // Group by site_type so an archive with many same-engine subtree rows
  // (e.g. a single-engine wiki where each /page-name/ becomes its own
  // archive_path_types row because sibling consolidation can't collapse
  // to root without breaking hybrid-engine detection) renders as one
  // pill per engine rather than dozens. Multi-row groups link to the
  // unfiltered tree view; single-row groups keep the original prefix
  // link so hybrid-host archives still drill into the right subtree.
  const groups = new Map<string, ArchiveSiteType[]>();
  for (const r of rows) {
    const list = groups.get(r.site_type);
    if (list) list.push(r);
    else groups.set(r.site_type, [r]);
  }

  const pillStyle = {
    fontFamily: 'monospace',
    fontSize: 12,
    padding: '4px 10px',
    border: '1px solid var(--pill-border-tag)',
    borderRadius: 4,
    color: 'var(--pill-fg-tag)',
    background: 'var(--pill-bg-tag)',
    textDecoration: 'none',
  } as const;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 14 }}>Detected engines</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Array.from(groups.entries()).map(([siteType, groupRows]) => {
          if (groupRows.length === 1) {
            const r = groupRows[0];
            return (
              <Link
                key={`${r.path_prefix}:${r.site_type}`}
                to={`/archives/${archiveId}/tree?prefix=${encodeURIComponent(r.path_prefix)}`}
                style={pillStyle}
              >
                <strong>{r.site_type}</strong> {r.path_prefix}
                <span className="muted" style={{ marginLeft: 6 }}>
                  {r.distinct_url_count.toLocaleString()} URLs, {fmtBytes(r.total_bytes)}
                </span>
              </Link>
            );
          }
          const urls = groupRows.reduce((s, r) => s + r.distinct_url_count, 0);
          const bytes = groupRows.reduce((s, r) => s + r.total_bytes, 0);
          const title = groupRows.map((r) => r.path_prefix).join('\n');
          return (
            <Link
              key={siteType}
              to={`/archives/${archiveId}/tree`}
              title={title}
              style={pillStyle}
            >
              <strong>{siteType}</strong> {groupRows.length} subtrees
              <span className="muted" style={{ marginLeft: 6 }}>
                {urls.toLocaleString()} URLs, {fmtBytes(bytes)}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}


/** Admin-only per-archive URL exclude regex. The crawler runs the
 *  pattern (Python re.search) over every URL — matches are dropped
 *  before enqueue and also skipped at dequeue, so already-queued URLs
 *  stop being fetched once the pattern is set. Useful for cutting noise
 *  paths out of a crawl (`/order`, `/cart`, `/print=1`, …) without
 *  coding a site_type rule. */
function UrlExcludeRegexCard({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [value, setValue] = useState(archive.url_exclude_regex ?? '');
  const [busy, setBusy] = useState<'save' | 'preview' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<{ pattern: string; sample_size: number; matched: number; samples: string[] } | null>(null);

  // Resync if the archive prop changes underneath us (e.g. after a
  // Pause/Resume action returns a fresh object).
  useEffect(() => {
    setValue(archive.url_exclude_regex ?? '');
    setError(null);
    setSaved(false);
    setPreview(null);
  }, [archive.url_exclude_regex]);

  const current = archive.url_exclude_regex ?? '';
  const dirty = value !== current;
  const trimmed = value.trim();

  async function save() {
    setBusy('save');
    setError(null);
    setSaved(false);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/url-exclude-regex`, {
        method: 'POST',
        body: { url_exclude_regex: trimmed === '' ? null : value },
      });
      onUpdated(data);
      setSaved(true);
      setPreview(null);
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Save failed (${err.status}).`);
      } else {
        setError('Save failed.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function runPreview() {
    if (!trimmed) return;
    setBusy('preview');
    setError(null);
    setSaved(false);
    try {
      const resp = await api<{ sample_size: number; matched: number; samples: string[] }>(
        `/archives/${archive.id}/url-exclude-regex/preview`,
        { method: 'POST', body: { url_exclude_regex: value } },
      );
      setPreview({ pattern: value, ...resp });
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Preview failed (${err.status}).`);
      } else {
        setError('Preview failed.');
      }
    } finally {
      setBusy(null);
    }
  }

  // Preview is only meaningful while the input still matches what was
  // tested — once the user keeps typing, hide the stale result.
  const previewStale = preview !== null && preview.pattern !== value;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 14 }}>URL exclude regex</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Python regex (re.search) tested against each newly-discovered URL before it goes in the queue.
        Matches are dropped. Example: <code>/order</code> skips every URL containing <code>/order</code>.
        Leave blank to disable. Applies to already-queued URLs too — matches are skipped when dequeued.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); setSaved(false); }}
          placeholder="(no filter)"
          style={{ flex: 1, minWidth: 280, fontFamily: 'monospace', fontSize: 13 }}
          disabled={busy !== null}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button
          className="secondary"
          onClick={runPreview}
          disabled={busy !== null || !trimmed}
          title="Test this pattern against the last 100 captured URLs to see how many would have been skipped."
        >
          {busy === 'preview' ? 'Testing…' : 'Preview'}
        </button>
        <button onClick={save} disabled={busy !== null || !dirty}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
      </div>
      {error && <p style={{ color: '#900', fontSize: 13, marginTop: 8 }}>{error}</p>}
      {saved && !dirty && (
        <p style={{ color: '#070', fontSize: 13, marginTop: 8 }}>
          Saved.{current ? '' : ' Filter cleared.'}
        </p>
      )}
      {preview && !previewStale && (
        <div style={{ marginTop: 8, padding: 8, background: 'var(--pill-bg-tag)', border: '1px solid var(--pill-border-tag)', borderRadius: 4, fontSize: 13 }}>
          <strong>{preview.matched}</strong> of the last <strong>{preview.sample_size}</strong> captured URLs would have matched.
          {preview.samples.length > 0 && (
            <ul style={{ margin: '6px 0 0 16px', padding: 0, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {preview.samples.map((s) => <li key={s}>{s}</li>)}
              {preview.matched > preview.samples.length && (
                <li style={{ listStyle: 'none', color: 'var(--fg-muted)', fontFamily: 'inherit' }}>
                  …and {preview.matched - preview.samples.length} more
                </li>
              )}
            </ul>
          )}
        </div>
      )}
      {previewStale && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Pattern changed — click Preview to re-test.
        </p>
      )}
    </div>
  );
}

function AuthControls({
  archive,
  onUpdated,
}: {
  archive: Archive;
  onUpdated: (a: Archive) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [handler, setHandler] = useState(archive.login_handler ?? 'memberpress');
  const [username, setUsername] = useState(archive.login_username ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'login' | 'relogin' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const isFailed = archive.status === 'auth_failed';
  // The archive may not have credentials at all yet — operator added it
  // without login because most content is public, but is now adding
  // creds retroactively to unlock the auth-walled bits (private threads,
  // attachments behind a login wall, etc.).
  const hasCredentials = !!archive.login_username;

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy('login');
    setError(null);
    setInfo(null);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/login`, {
        method: 'POST',
        body: { login_handler: handler, login_username: username, login_password: password },
      });
      onUpdated(data);
      setEditing(false);
      setPassword('');
      setInfo('Credentials saved and login verified.');
    } catch (err) {
      const e = err as { payload?: { errors?: Record<string, string[]>; message?: string }; status?: number };
      const first = e.payload?.errors && Object.values(e.payload.errors)[0]?.[0];
      setError(first ?? e.payload?.message ?? `Update failed (${e.status ?? '?'}).`);
    } finally {
      setBusy(null);
    }
  }

  async function relogin() {
    setBusy('relogin');
    setError(null);
    setInfo(null);
    try {
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}/relogin`, { method: 'POST' });
      onUpdated(data);
      setInfo('Cached session cleared — the worker will log in fresh on the next fetch.');
    } catch (err) {
      const e = err as { status?: number };
      setError(`Re-login failed (${e.status ?? '?'}).`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ background: isFailed ? 'var(--pill-bg-error)' : undefined }}>
      <h3 style={{ margin: 0 }}>{isFailed ? 'Authentication failed' : 'Site login'}</h3>
      <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        {isFailed
          ? 'The crawler couldn\'t maintain a session. Update the credentials below to resume crawling.'
          : hasCredentials ? (
            <>
              <>Logged in as <strong>{archive.login_username}</strong>. </>
              {archive.last_login_at && <>Last login: {new Date(archive.last_login_at).toLocaleString()}.</>}
            </>
          ) : (
            <>No credentials attached. Add them if the site requires login to reach some content (private threads, attachments behind a wall, etc.) — already-captured pages aren't refetched.</>
          )}
      </p>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {info && <div className="muted" style={{ marginBottom: 12, color: '#0a7' }}>{info}</div>}

      {!editing && !isFailed && (
        <div style={{ display: 'flex', gap: 8 }}>
          {hasCredentials && (
            <button onClick={relogin} disabled={busy !== null}>
              {busy === 'relogin' ? '…' : 'Re-login now'}
            </button>
          )}
          <button onClick={() => setEditing(true)} disabled={busy !== null} className="secondary">
            {hasCredentials ? 'Update credentials' : 'Add credentials'}
          </button>
        </div>
      )}

      {(editing || isFailed) && (
        <form onSubmit={submitLogin} style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <label htmlFor="auth-handler">Login type</label>
            <select id="auth-handler" value={handler} onChange={(e) => setHandler(e.target.value)}>
              <option value="memberpress">MemberPress (WordPress)</option>
              <option value="xenforo">XenForo</option>
              <option value="invision">Invision Community (IPS 4.x)</option>
            </select>

            <label htmlFor="auth-username">Username</label>
            <input id="auth-username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" required />

            <label htmlFor="auth-password">Password</label>
            <input id="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" required />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy !== null}>
              {busy === 'login' ? 'Verifying…' : 'Test login & save'}
            </button>
            {!isFailed && (
              <button type="button" onClick={() => setEditing(false)} disabled={busy !== null} className="secondary">
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function DeleteArchiveButton({ archive }: { archive: Archive }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function onDelete() {
    // Two-step confirm — first explains the blast radius, second is the
    // actual go-ahead. Deletion runs in the background: the archive flips
    // to 'deleting' while a job purges its R2 storage (objects not shared
    // with another archive), clears the search index, and finally drops
    // the row and its crawl_queue / crawl_errors / snapshots / metrics.
    if (!window.confirm(
      `Permanently delete this archive (${archive.domain})?\n\nAll snapshots, queue rows, errors, and search data will be removed, and the stored bytes are purged from R2 (except any shared with another archive). For a large archive this runs in the background and can take a while. This cannot be undone.`,
    )) return;
    if (!window.confirm('Last chance — delete now?')) return;

    setBusy(true);
    try {
      await api(`/archives/${archive.id}`, { method: 'DELETE' });
      navigate('/');
    } catch {
      window.alert('Delete failed.');
      setBusy(false);
    }
  }

  return (
    <button
      onClick={onDelete}
      disabled={busy}
      className="secondary"
      style={{ background: 'var(--pill-bg-error)', color: 'var(--pill-fg-error)', borderColor: 'var(--pill-border-stalled)' }}
      title="Permanently delete this archive and all its data"
    >
      {busy ? 'Deleting…' : 'Delete'}
    </button>
  );
}

function TransferOwnerButton({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Reassign this archive to another user"
      >
        Transfer owner
      </button>
      {open && (
        <TransferOwnerDialog
          archiveId={archive.id}
          domain={archive.domain}
          currentOwnerId={archive.created_by}
          onClose={() => setOpen(false)}
          onTransferred={() => {
            api<{ data: Archive }>(`/archives/${archive.id}`)
              .then(({ data }) => onUpdated(data))
              .catch(() => { /* page is still usable; user can refresh */ });
          }}
        />
      )}
    </>
  );
}

function RecrawlButton({ archive, onUpdated }: { archive: Archive; onUpdated: (a: Archive) => void }) {
  const [busy, setBusy] = useState(false);
  const rc = recrawlState(archive);
  // Running = block (avoid clobbering counters with a second pass).
  // Stalled = allow click — it's the operator's "kick it" button. Done
  // and idle = allow click as the normal path.
  const inFlight = rc.state === 'running';

  async function onClick() {
    if (!window.confirm(
      'Recrawl this archive? Walks every previously-captured HTML page, runs the current extractors over it, and queues anything new — useful after the crawler grows new extractor capabilities (e.g. capturing scripts / stylesheets) you want applied retroactively. Idempotent: already-known URLs are skipped.',
    )) return;
    setBusy(true);
    try {
      await api(`/archives/${archive.id}/recrawl`, { method: 'POST' });
      // Refresh so the running indicator + polling activate immediately.
      const { data } = await api<{ data: Archive }>(`/archives/${archive.id}`);
      onUpdated(data);
    } catch {
      window.alert('Failed to start recrawl.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={busy || inFlight}
      className="secondary"
      title={inFlight
        ? 'A recrawl is already running on this archive — watch the status line above the action buttons for progress.'
        : 'Re-run all current extractors over previously-captured HTML and queue anything newly discovered.'}
    >
      {busy ? 'Starting…' : inFlight ? 'Recrawling…' : rc.state === 'stalled' ? 'Restart recrawl' : 'Recrawl'}
    </button>
  );
}

/** Inline search box scoped to this archive. Submitting jumps to the
 *  Search page with archive_id pre-set so the user only sees hits from
 *  the archive they're currently looking at. */
function ArchiveSearchBox({ archive }: { archive: Archive }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    if (q.length < 2) return;
    const params = new URLSearchParams({
      q,
      archive_id: String(archive.id),
      archive_domain: archive.domain,
    });
    navigate(`/search?${params.toString()}`);
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', gap: 6, marginTop: -4, marginBottom: 8 }}>
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`Search within ${archive.domain}…`}
        style={{ flex: '0 1 320px', padding: '4px 8px', fontSize: 13 }}
        aria-label={`Search within ${archive.domain}`}
      />
      <button type="submit" disabled={draft.trim().length < 2} className="secondary" style={{ fontSize: 13 }}>
        Search
      </button>
    </form>
  );
}

/** Tag pills row — approved tags above, pending proposals below with
 *  vote controls. Any approved user can propose a tag (creates pending);
 *  admins can also approve/reject pending proposals directly. */
function ArchiveTags({ archiveId, canManage }: { archiveId: number; canManage: boolean }) {
  const [tags, setTags] = useState<ArchiveTag[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const { data } = await api<{ data: ArchiveTag[] }>(`/archives/${archiveId}/tags`);
    setTags(data);
  }

  useEffect(() => { load(); }, [archiveId]);

  async function addByName(raw: string) {
    const name = raw.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/archives/${archiveId}/tags`, {
        method: 'POST',
        body: { tag_name: name },
      });
      setDraft('');
      setAdding(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Add failed (${err.status}).`);
      } else {
        setError('Add failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(tag: ArchiveTag) {
    if (!tag.assignment_id) return;
    if (!window.confirm(`Remove tag "${tag.name}" from this archive?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/archives/${archiveId}/tags/${tag.assignment_id}`, { method: 'DELETE' });
      await load();
    } catch {
      setError('Remove failed.');
    } finally {
      setBusy(false);
    }
  }

  async function voteOnAssignment(assignmentId: number, value: -1 | 0 | 1) {
    await api(`/archives/${archiveId}/tags/${assignmentId}/vote`, { method: 'POST', body: { value } });
    await load();
  }

  async function adminApproveAssignment(assignmentId: number) {
    await api(`/archives/${archiveId}/tags/${assignmentId}/approve`, { method: 'POST' });
    await load();
  }

  async function adminRejectAssignment(assignmentId: number) {
    await api(`/archives/${archiveId}/tags/${assignmentId}/reject`, { method: 'POST' });
    await load();
  }

  if (tags === null) return null;

  const approved = tags.filter((t) => t.assignment_status === 'approved');
  const pending = tags.filter((t) => t.assignment_status === 'pending');

  // Empty + can't propose → render nothing. canManage is admin; non-admins
  // also get the propose UI now, so we only fully hide for that combo
  // — but in practice every approved user can propose, so this branch
  // exists mostly for the loading edge.
  if (approved.length === 0 && pending.length === 0 && !canManage) {
    // Non-admin viewer with no tags still gets the propose row below.
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span className="muted" style={{ fontSize: 12, marginRight: 2 }}>Tags:</span>
        {approved.length === 0 && <span className="muted" style={{ fontSize: 12 }}>none yet</span>}
        {approved.map((t) => (
          <span key={t.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 999,
            background: 'var(--pill-bg-tag)', border: '1px solid var(--pill-border-tag)',
            fontSize: 12,
          }}>
            <Link to={`/tags/${encodeURIComponent(t.name)}`} style={{ color: 'var(--pill-fg-tag)', textDecoration: 'none' }}>
              {t.name}
            </Link>
            {canManage && (
              <button
                type="button"
                onClick={() => onRemove(t)}
                title={`Remove "${t.name}"`}
                className="link-button"
                style={{ background: 'none', border: 0, color: 'var(--fg-error)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}
                disabled={busy}
              >×</button>
            )}
          </span>
        ))}
        {adding ? (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <TagAutocomplete
              value={draft}
              onChange={setDraft}
              onSubmit={addByName}
              excludeNames={tags.map((t) => t.name)}
              placeholder="tag name"
              maxLength={30}
              autoFocus
              disabled={busy}
              width={160}
            />
            <button
              type="button"
              onClick={() => addByName(draft)}
              disabled={busy || !draft.trim()}
              className="secondary"
              style={{ padding: '2px 8px', fontSize: 12 }}
            >{canManage ? 'Add' : 'Propose'}</button>
            <button
              type="button"
              onClick={() => { setAdding(false); setDraft(''); setError(null); }}
              className="secondary"
              style={{ padding: '2px 8px', fontSize: 12 }}
            >Cancel</button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="link-button"
            title={canManage ? 'Add a tag' : 'Propose a tag (goes to community vote)'}
            style={{ background: 'none', border: '1px dashed var(--border)', borderRadius: 999, color: 'var(--fg-muted)', cursor: 'pointer', fontSize: 12, padding: '2px 10px' }}
          >{canManage ? '+ add' : '+ propose'}</button>
        )}
        {error && <span className="error" style={{ fontSize: 12 }}>{error}</span>}
      </div>

      {pending.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
          <span className="muted" style={{ fontSize: 12, marginRight: 2 }}>
            Awaiting review (+3 to approve / −3 to reject):
          </span>
          {pending.map((t) => (
            <span key={t.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '2px 8px', borderRadius: 999,
              background: 'var(--bg-pending-pill)', border: '1px dashed var(--border-pending-pill)',
              fontSize: 12,
            }}>
              <span style={{ color: 'var(--border-pending-pill-text)' }}>{t.name}</span>
              {t.assignment_id != null && (
                <VoteControl
                  score={t.score ?? 0}
                  userVote={t.user_vote ?? null}
                  onVote={(v) => voteOnAssignment(t.assignment_id!, v)}
                />
              )}
              {canManage && t.assignment_id != null && (
                <>
                  <button
                    type="button"
                    onClick={() => adminApproveAssignment(t.assignment_id!)}
                    className="link-button"
                    title="Force-approve (bypasses voting)"
                    style={{ background: 'none', border: 0, color: '#0a7', cursor: 'pointer', fontSize: 12, padding: 0, marginLeft: 2 }}
                  >✓</button>
                  <button
                    type="button"
                    onClick={() => adminRejectAssignment(t.assignment_id!)}
                    className="link-button"
                    title="Force-reject (bypasses voting)"
                    style={{ background: 'none', border: 0, color: '#c33', cursor: 'pointer', fontSize: 12, padding: 0 }}
                  >✗</button>
                </>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function IndexForSearchButton({ archive }: { archive: Archive }) {
  const [busy, setBusy] = useState(false);
  const idx = indexerState(archive);
  const isRunning = idx.state === 'running';
  const isStalled = idx.state === 'stalled';

  async function onClick() {
    const verb = isStalled
      ? 'Restart the stalled indexer'
      : archive.text_indexed_at
        ? 'Re-index'
        : 'Build the search index for';
    if (!window.confirm(
      `${verb} this archive? Walks every URL's latest snapshot, extracts plain text, and (re)populates snapshot_text. Runs in the background; large archives take a few minutes.`,
    )) return;
    setBusy(true);
    try {
      await api(`/archives/${archive.id}/index-for-search`, { method: 'POST' });
      window.alert('Search indexing started in the background. The status above will switch back to "Search index built" when it completes.');
    } catch {
      window.alert('Failed to start indexing.');
    } finally {
      setBusy(false);
    }
  }

  const label = busy
    ? 'Starting…'
    : isStalled
      ? 'Restart indexer'
      : isRunning
        ? 'Indexing…'
        : archive.text_indexed_at
          ? 'Re-index search'
          : 'Index for search';

  return (
    <button
      onClick={onClick}
      disabled={busy || isRunning}
      className="secondary"
      style={isStalled ? { borderColor: '#c33', color: '#c33' } : undefined}
      title={isStalled
        ? 'The previous indexer subprocess died (OOM, segfault, etc.) and didn\'t clean up. Click to spawn a fresh one — it picks up where the old one left off.'
        : isRunning
          ? 'A search-index pass is currently running. The page will reflect completion on next refresh.'
          : 'Extract plain text from every URL\'s latest snapshot and populate the FULLTEXT search index. Auto-runs when an archive completes; this button covers backfills and re-indexes.'}
    >
      {label}
    </button>
  );
}
